import { Component, OnInit } from '@angular/core';
import { to } from 'await-to-js';

import { CbMyProfileService } from './../../services/cb-myprofile.service';
import { CbBackendService } from './../../services/cb-backend.service';
import { CbWebApi } from '../../services/cb-webapi';
import { CbAuthURLService } from '../../services/cb-auth-url.service';
import { LogFactory } from '../../services/cb-debug';
import { whenLfxAnalyticsReady } from '../../services/lfx-analytics';
const log = LogFactory('CbCventUserTypeComponent');

// Longest a click on "register" waits for a pending Identify before leaving the page.
const IDENTIFY_BEFORE_REDIRECT_MS = 1500;

interface DecodedToken {
  iat: number;
  iss: string;
  sub: string;
  exp: number;
  ip: string;
  aud: string;
  continueType: string;
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  avatar: string;
}


@Component({
  selector: 'cb-cvent-user-type',
  templateUrl: './cvent-user-type.component.html',
  styleUrls: ['./cvent-user-type.component.scss'],
})
export class CbCventUserTypeComponent implements OnInit {
  userEmails: string[] = [];
  selectedEmail = '';
  isLoading = true;
  errorMessage = '';
  private identifyDone: Promise<void> = Promise.resolve();

  constructor(
    private cbAuthURLService: CbAuthURLService,
    private cbBackendService: CbBackendService,
    private cbWebApi: CbWebApi,
    private cbMyProfileService: CbMyProfileService
  ) {
    // document.title = 'Linux Foundation - Cvent';
  }

  ngOnInit() {
    const canContinue = this.cbWebApi.verifyUserCanContinue();
    if (!canContinue) {
      // * user will be redirected to authorize page
      return;
    }

    this.cbMyProfileService.displayMyProfileURL();

    this.setUserEmails();
    this.setInitialValues();
  }

  setInitialValues() {
    const decoded = this.cbAuthURLService.getDecodedToken() as DecodedToken;

    this.selectedEmail = decoded.email;
    this.userEmails = [decoded.email];
    this.setSegmentIdentification(decoded);
  }

  setSegmentIdentification(decoded: DecodedToken) {
    const userObjectFromDecodedToken = {
      ...decoded,
      // INFO: Guard against missing claims
      'https://sso.linuxfoundation.org/claims/username': decoded.username,
      email: decoded.email,
    };

    this.identifyDone = this.identifyWhenAnalyticsReady(userObjectFromDecodedToken);
  }

  private async identifyWhenAnalyticsReady(user: object): Promise<void> {
    try {
      const analytics = await whenLfxAnalyticsReady();
      if (!analytics) {
        log('LfxAnalytics not available; Identify skipped');
        return;
      }
      await analytics.identifyAuth0User(user);
    } catch (error) {
      log(`Failed to identify user in LfxAnalytics: ${error}`);
    }
  }

  async setUserEmails() {
    log('entered setUserEmails');
    const { token } = this.cbAuthURLService.getAllURLParams();
    const [err, emails] = await to(this.cbBackendService.getUserEmails(token).toPromise());

    this.loadingEnd();

    if (err || !emails || !(emails as string[]).length) {
      // todo: maybe alert to the user
      return;
    }
    this.userEmails = emails as any;
  }

  registerMyself() {
    this.continueWithType('no');
  }

  registerSomeoneElse() {
    this.continueWithType('yes');
  }

  loading() {
    this.errorMessage = '';
    this.isLoading = true;
  }

  loadingEnd() {
    this.isLoading = false;
  }
  setErrorMessage(message = 'There was a problem, try again later') {
    this.isLoading = false;
    this.errorMessage = message;
  }

  async continueWithType(type: string = 'no') {
    log('entered continueWithType');

    if (this.isLoading) {
      return;
    }

    this.loading();

    const { token, state } = this.cbAuthURLService.getAllURLParams();

    const selectedEmail = this.selectedEmail;

    const [err, response] = await to(this.cbBackendService.createEmailToken(token, selectedEmail).toPromise());

    if (err) {
      this.isLoading = false;
      return this.setErrorMessage(
        'There was a problem with the email you selected. Please Try sign out and Sign In again or Contact us to help you.'
      );
    }

    const extraParam = {
      cventType: type,
    };

    // Give a pending Identify a moment to go out before leaving the page.
    await Promise.race([this.identifyDone, new Promise((resolve) => setTimeout(resolve, IDENTIFY_BEFORE_REDIRECT_MS))]);
    this.cbWebApi.continueToAuth0(response.token, state, extraParam);
  }

  signOut() {
    this.cbWebApi.returnToAuthorizePage();
  }
}
